import clsx from 'clsx';
import React from 'react';

import Clipper from '@components/Clipper';
import Comments from '@components/Comments';
import VerticalAdvertisement from '@components/FeaturedPartnerAd/Vertical';
import type { MidArticleEventProps } from '@components/pages/Article/MidArticle/Event/interfaces';
import type { Env } from '@contexts/AppContext';
import useCurrentUser from '@hooks/useCurrentUser';
import useServerSideRendering from '@hooks/useServerSideRendering';
import type { Article, Pickup, ScriptList } from '@interfaces/homepage';
import { TrackingLocation } from '@tracking';
import { CommentableType } from '@utils/backend-api/comments';
import { getArticleImgSrc } from '@utils/images';

import ArticlePickups from '../ArticlePickups';
import Body from '../Body';
import EndOfArticleDeepResearch from '../EndOfArticle/DeepResearch';
import { MidArticleUnitTypes } from '../interfaces';

interface DataContentProps {
  addendum: Article['addendum'];
  advertisement?: Article['advertisement'];
  authors: Article['authors'];
  bodyClassName?: string;
  canViewFullArticle: boolean;
  commentsCount?: number;
  env: Env;
  freeBlurb: Article['freeBlurb'];
  fullText: Article['fullText'];
  highlight?: string;
  id: number;
  isSEOBotAndArticleIsNotFree: boolean;
  midArticleUnitType?: MidArticleUnitTypes;
  newsletterId?: string | number;
  picture?: string;
  pickups?: Pickup[];
  relatedEvent?: MidArticleEventProps;
  scriptList?: ScriptList[];
  takeaway: Article['takeaway'];
}

const DataContent = ({
  addendum,
  advertisement,
  authors,
  bodyClassName,
  canViewFullArticle,
  commentsCount,
  env,
  freeBlurb,
  fullText,
  highlight,
  id,
  isSEOBotAndArticleIsNotFree,
  picture,
  pickups,
  relatedEvent,
  scriptList,
  takeaway,
}: DataContentProps): JSX.Element => {
  const hydrated = useServerSideRendering();
  const { currentUser } = useCurrentUser();
  const clipperProps = {
    contentSelector: '.clipperable-content',
    contentImgixUrl: getArticleImgSrc(id, picture, env),
    logoUrl: `${env.IMGIX_URL}/logos/theinformation/the-information-logo-wordmark.svg`,
  };

  return (
    <>
      <div
        className={clsx('flex flex-col md:flex-row md:gap-72 xl:gap-112', {
          'mb-80': !canViewFullArticle,
        })}
      >
        <div className="flex flex-1 flex-col">
          <div className="md:ml-[60px] xl:ml-[84px]">
            <Body
              addendum={addendum}
              articleId={id}
              authors={authors}
              canViewFullArticle={canViewFullArticle}
              className={bodyClassName}
              freeBlurb={freeBlurb}
              fullText={fullText}
              highlight={highlight}
              isSEOBotAndArticleIsNotFree={isSEOBotAndArticleIsNotFree}
              relatedEvent={relatedEvent}
              scriptList={scriptList}

              takeaway={takeaway}
            />
            {canViewFullArticle && <EndOfArticleDeepResearch id={id} />}
            {canViewFullArticle && pickups?.length > 0 && <ArticlePickups pickups={pickups} />}
          </div>
        </div>
        <div className="flex-shrink-0 md:w-[195px] lg:w-[260px] print:hidden">
          {advertisement && (
            <VerticalAdvertisement
              advertisement={advertisement}
              className="mt-64 hidden md:block"
              trackingLocation={TrackingLocation.SideFeed}
            />
          )}
        </div>
      </div>
      {hydrated && currentUser?.is_subscriber && (
        <div className="mr-0 mt-80 sm:mt-112 md:mr-[267px] lg:mr-[372px] print:hidden">
          <Comments
            commentableId={id}
            commentableType={CommentableType.Article}
            commentsCount={commentsCount}
          />
        </div>
      )}
      {canViewFullArticle && advertisement && (
        <div className="mx-48">
          <VerticalAdvertisement
            advertisement={advertisement}
            className="mt-48 block md:hidden"
            trackingLocation={TrackingLocation.ContentBody}
          />
        </div>
      )}
      {hydrated && <Clipper {...clipperProps} env={env} />}
    </>
  );
};

export default DataContent;
